import React, { useState } from 'react';
import styled from 'styled-components';
import { FieldScheduleEntry, formatTime } from '../../types/schedule';

interface UpcomingEvent {
  id: string;
  title: string;
  date: string;
  startTime: string;
  endTime: string;
}

interface CreateFieldNewsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (newsData: CreateNewsData) => void;
  editingNews?: any; // FieldNewsDto type from service
  upcomingEvents?: UpcomingEvent[];
}

export interface CreateNewsData {
  title: string;
  content: string;
  isPriority: boolean;
  imageUrls: string[];
  imageFile?: File;
  linkedEventId?: string;
}

const ModalOverlay = styled.div<{ isOpen: boolean }>`
  display: ${props => props.isOpen ? 'flex' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: #1a2a1a;
  border: 1px solid #00d4ff;
  border-radius: 8px;
  padding: 30px;
  width: 90%;
  max-width: 600px;
  max-height: 80vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 25px;
`;

const ModalTitle = styled.h2`
  color: #00d4ff;
  margin: 0;
  font-size: 1.5rem;
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  color: #00d4ff;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s;

  &:hover {
    transform: scale(1.1);
  }
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  color: #b0b0b0;
  margin-bottom: 8px;
  font-size: 0.9rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px;
  background: #0a0f0a;
  border: 1px solid #333;
  border-radius: 4px;
  color: white;
  font-size: 1rem;
  transition: border-color 0.3s;

  &:focus {
    outline: none;
    border-color: #00d4ff;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 10px;
  background: #0a0f0a;
  border: 1px solid #333;
  border-radius: 4px;
  color: white;
  font-size: 1rem;
  min-height: 150px;
  resize: vertical;
  transition: border-color 0.3s;

  &:focus {
    outline: none;
    border-color: #00d4ff;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 10px;
  background: #0a0f0a;
  border: 1px solid #333;
  border-radius: 4px;
  color: white;
  font-size: 1rem;
  transition: border-color 0.3s;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: #00d4ff;
  }

  option {
    background: #0a0f0a;
    color: white;
  }
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 20px;
`;

const Checkbox = styled.input`
  width: 20px;
  height: 20px;
  cursor: pointer;
`;

const CheckboxLabel = styled.label`
  color: #b0b0b0;
  font-size: 0.9rem;
  cursor: pointer;
  user-select: none;

  span {
    color: #ffaa00;
    font-weight: bold;
  }
`;

const ImageUrlList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 10px;
`;

const ImageUrlItem = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px;
  background: #0a0f0a;
  border: 1px solid #333;
  border-radius: 4px;
`;

const RemoveButton = styled.button`
  background: #ff4444;
  border: none;
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.8rem;
  transition: background 0.3s;

  &:hover {
    background: #ff6666;
  }
`;


const ButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 15px;
  margin-top: 30px;
`;

const CancelButton = styled.button`
  background: transparent;
  border: 1px solid #666;
  color: #b0b0b0;
  padding: 10px 25px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 1rem;
  transition: all 0.3s;

  &:hover {
    border-color: #00d4ff;
    color: #00d4ff;
  }
`;

const SubmitButton = styled.button`
  background: linear-gradient(135deg, #00d4ff, #0099cc);
  border: none;
  color: white;
  padding: 10px 25px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 1rem;
  font-weight: bold;
  transition: all 0.3s;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(0, 212, 255, 0.3);
  }

  &:disabled {
    background: #666;
    cursor: not-allowed;
    transform: none;
  }
`;

const FileUploadContainer = styled.div`
  border: 2px dashed #333;
  border-radius: 8px;
  padding: 20px;
  text-align: center;
  transition: border-color 0.3s;

  &:hover {
    border-color: #00d4ff;
  }
`;

const FileInput = styled.input`
  display: none;
`;

const FileUploadButton = styled.label`
  background: linear-gradient(135deg, #0d7377, #14a085);
  border: none;
  color: white;
  padding: 10px 20px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9rem;
  transition: all 0.3s;
  display: inline-block;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(20, 160, 133, 0.3);
  }
`;

const FileList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 15px;
`;

const FileItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  background: #0a0f0a;
  border: 1px solid #333;
  border-radius: 4px;
  font-size: 0.9rem;
`;


const CreateFieldNewsModal: React.FC<CreateFieldNewsModalProps> = ({ isOpen, onClose, onSubmit, editingNews, upcomingEvents = [] }) => {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [isPriority, setIsPriority] = useState(false);
  const [imageUrls, setImageUrls] = useState<string[]>([]);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [linkedEventId, setLinkedEventId] = useState<string>('');

  // Initialize form with editing data when modal opens
  React.useEffect(() => {
    if (isOpen && editingNews) {
      setTitle(editingNews.title || '');
      setContent(editingNews.content || '');
      setIsPriority(editingNews.isPriority || false);
      setLinkedEventId(editingNews.linkedEventId || '');
      // Используем imageUrls если есть, иначе fallback на imageUrl
      const existingImages = editingNews.imageUrls && editingNews.imageUrls.length > 0
        ? editingNews.imageUrls
        : editingNews.imageUrl
          ? [editingNews.imageUrl]
          : [];
      setImageUrls(existingImages);
    } else if (isOpen && !editingNews) {
      // Reset form for creating new news
      setTitle('');
      setContent('');
      setIsPriority(false);
      setLinkedEventId('');
      setImageUrls([]);
      setSelectedFiles([]);
    }
  }, [isOpen, editingNews]);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      const fileList = Array.from(files);
      // Ограничиваем только одним изображением
      if (imageUrls.length === 0 && selectedFiles.length === 0) {
        setSelectedFiles([fileList[0]]); // Берем только первый файл
      }
    }
    // Сбрасываем input для возможности выбора того же файла
    event.target.value = '';
  };

  const handleSubmit = async () => {
    if (title.trim() && content.trim()) {
      onSubmit({
        title: title.trim(),
        content: content.trim(),
        isPriority,
        imageUrls,
        imageFile: selectedFiles.length > 0 ? selectedFiles[0] : undefined,
        linkedEventId: linkedEventId || undefined
      });

      // Reset form
      setTitle('');
      setContent('');
      setIsPriority(false);
      setLinkedEventId('');
      setImageUrls([]);
      setSelectedFiles([]);
      onClose();
    }
  };

  const handleClose = () => {
    // Reset form on close
    setTitle('');
    setContent('');
    setIsPriority(false);
    setLinkedEventId('');
    setImageUrls([]);
    setSelectedFiles([]);
    onClose();
  };

  // Format event date for display
  const formatEventDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <ModalOverlay isOpen={isOpen} onClick={handleClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>{editingNews ? 'Edit Field News' : 'Create Field News'}</ModalTitle>
          <CloseButton onClick={handleClose}>×</CloseButton>
        </ModalHeader>

        <FormGroup>
          <Label>Title *</Label>
          <Input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Enter news title..."
          />
        </FormGroup>

        <FormGroup>
          <Label>Content *</Label>
          <TextArea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="Enter news content..."
          />
        </FormGroup>

        <CheckboxContainer>
          <Checkbox
            type="checkbox"
            id="isPriority"
            checked={isPriority}
            onChange={(e) => setIsPriority(e.target.checked)}
          />
          <CheckboxLabel htmlFor="isPriority">
            Mark as <span>Priority News</span> (will be highlighted)
          </CheckboxLabel>
        </CheckboxContainer>

        {upcomingEvents.length > 0 && (
          <FormGroup>
            <Label>Link to Event (optional)</Label>
            <Select
              value={linkedEventId}
              onChange={(e) => setLinkedEventId(e.target.value)}
            >
              <option value="">-- No linked event --</option>
              {upcomingEvents.map(event => (
                <option key={event.id} value={event.id}>
                  {event.title} - {formatEventDate(event.date)} ({formatTime(event.startTime)} - {formatTime(event.endTime)})
                </option>
              ))}
            </Select>
            <p style={{ color: '#b0b0b0', margin: '5px 0 0 0', fontSize: '0.8rem' }}>
              Link this news to an upcoming event to show it in the Upcoming Events section
            </p>
          </FormGroup>
        )}

        <FormGroup>
          <Label>Image (optional)</Label>
          <FileUploadContainer>
            <FileInput
              id="image-upload"
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              disabled={imageUrls.length > 0 || selectedFiles.length > 0}
            />
            <FileUploadButton 
              htmlFor="image-upload"
              style={{ 
                opacity: (imageUrls.length > 0 || selectedFiles.length > 0) ? 0.5 : 1,
                cursor: (imageUrls.length > 0 || selectedFiles.length > 0) ? 'not-allowed' : 'pointer'
              }}
            >
              📁 Select Image
            </FileUploadButton>
            <p style={{ color: '#b0b0b0', margin: '10px 0 0 0', fontSize: '0.8rem' }}>
              {(imageUrls.length > 0 || selectedFiles.length > 0) 
                ? 'Remove existing image to add a new one'
                : 'Select one image (JPEG, PNG, GIF - max 5MB) - will be uploaded automatically'
              }
            </p>
          </FileUploadContainer>

          {selectedFiles.length > 0 && (
            <>
              <FileList>
                <FileItem>
                  <span style={{ color: '#b0b0b0' }}>
                    {selectedFiles[0].name} ({(selectedFiles[0].size / 1024 / 1024).toFixed(2)} MB)
                  </span>
                  <RemoveButton onClick={() => setSelectedFiles([])}>
                    Remove
                  </RemoveButton>
                </FileItem>
              </FileList>
              <p style={{ color: '#00d4ff', fontSize: '0.8rem', margin: '5px 0' }}>
                ✅ Ready to upload with news
              </p>
            </>
          )}

          {imageUrls.length > 0 && (
            <>
              <Label style={{ marginTop: '20px' }}>Uploaded Image:</Label>
              <ImageUrlList>
                <ImageUrlItem>
                  <span style={{ color: '#b0b0b0', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {imageUrls[0].split('/').pop() || 'Uploaded image'}
                  </span>
                  <RemoveButton onClick={() => setImageUrls([])}>
                    Remove
                  </RemoveButton>
                </ImageUrlItem>
              </ImageUrlList>
            </>
          )}
        </FormGroup>

        <ButtonGroup>
          <CancelButton onClick={handleClose}>Cancel</CancelButton>
          <SubmitButton 
            onClick={handleSubmit} 
            disabled={!title.trim() || !content.trim()}
          >
            {editingNews ? 'Update News' : 'Create News'}
          </SubmitButton>
        </ButtonGroup>
      </ModalContent>
    </ModalOverlay>
  );
};

export default CreateFieldNewsModal;